/* eslint-disable no-nested-ternary */
import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AgreementDialogueHandler, DialogueHandlerProps } from './types';
import AgreementContext, { AgreementCtx } from './AgreementContext';
import useFetchAgreementDownload from '@/hooks/useApi/useFetchAgreementDownload';
import usePostVoidAgreement from '@/hooks/useApi/usePostVoidAgreement';
import { usePartnerData } from '@/providers/PartnerData.provider';
import AgreementDialogue from './AgreementDialogue';
import AgreementTable from './AgreementTable';
import styles from './Agreement.module.scss';

const { Box, Button, SubTitle, DropDown, Modal } = vp;

type AgreementProps = {
  agreementDetails: vp.AgreementDetailsResponse;
  accountToken: string;
  signingAuthorityInfo: vp.SigningAuthorityInfoResponse;
  onClick?: () => void;
};

type FC = FunctionComponent<AgreementProps>;

const AgreementContainer: FC = ({ agreementDetails, accountToken, signingAuthorityInfo, onClick }: AgreementProps) => {
  const history = useHistory();
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [shouldVoidAgreement, setShouldVoidAgreement] = useState(false);
  const [showVoidAgreementConfirmAlert, setShowVoidAgreementConfirmAlert] = useState(false);
  const [shouldViewAgreement, setShouldViewAgreement] = useState(false);
  const [showViewAgreementFailAlert, setShowViewAgreementFailAlert] = useState(false);
  const [shouldDownloadPDF, setShouldDownloadPDF] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [documentID, setDocumentID] = useState('');
  const [agreementPDF, fetchAgreementDownload] = useFetchAgreementDownload({ bearer: accountToken });
  const [voidAgreement, postVoidAgreement] = usePostVoidAgreement({ bearer: accountToken });
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const Agreement: AgreementCtx = {
    showVoidAgreementConfirmAlert,
    showViewAgreementFailAlert,
  };

  const handleMenu = () => {
    setIsMenuVisible(!isMenuVisible);
  };

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<AgreementDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: AgreementDialogueHandler = {
        void: {
          back: {
            default: () => {
              setShowVoidAgreementConfirmAlert(false);
            },
          },
          confirm: {
            default: () => {
              setShowVoidAgreementConfirmAlert(false);
              postVoidAgreement({ DocumentID: documentID });
            },
          },
        },
        view: {
          back: {
            default: () => {
              setShowViewAgreementFailAlert(false);
            },
          },
          confirm: {
            default: () => {
              setShowViewAgreementFailAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  useEffect(() => {
    setDocumentID(agreementDetails?.DocumentID || '');
  }, [agreementDetails]);

  // '/account/agreement/download'
  useEffect(() => {
    if (
      (shouldViewAgreement && agreementDetails?.AgreementStatus === 'Agreement Not Generated') ||
      (shouldViewAgreement && agreementDetails?.AgreementStatus === 'Agreement Voided')
    ) {
      setShouldViewAgreement(false);
      setShowViewAgreementFailAlert(true);
    } else if (shouldViewAgreement) {
      setShouldViewAgreement(false);
      setShouldDownloadPDF(true);
    }
  }, [shouldViewAgreement]);

  useEffect(() => {
    if (shouldDownloadPDF) {
      setShouldDownloadPDF(false);
      fetchAgreementDownload({
        DocumentID: documentID,
      });
    }
  }, [shouldDownloadPDF]);

  useEffect(() => {
    if (agreementPDF.Success) {
      setIsModalVisible(true);
    }
  }, [agreementPDF.Success]);

  // '/account/agreement/void'
  useEffect(() => {
    if (shouldVoidAgreement) {
      setShowVoidAgreementConfirmAlert(true);
      setShouldVoidAgreement(false);
    }
  }, [shouldVoidAgreement]);

  useEffect(() => {
    if (voidAgreement.Success) {
      history.goBack();
    }
  }, [voidAgreement.Success]);

  return (
    <>
      {signingAuthorityInfo.ErrorMessage === '' && agreementDetails.AgreementStatus === '' ? (
        <></>
      ) : signingAuthorityInfo.ErrorMessage === 'There is no signing authority information for this account' ? (
        <div className={styles.noDatacontainer}>
          <h2 className={styles.containerTitle}>Agreement</h2>
          <Box>
            Agreement is not formed due to no signor information on the account. Please add signors to start agreement
            drafting process.
          </Box>
          <Button
            id="add-signer-btn"
            label="Add Signer"
            color="primary"
            width="228px"
            noShadow
            round
            isDisabled={isRoleViewOnly}
            onClick={onClick}
          />
        </div>
      ) : (
        <AgreementContext.Provider value={Agreement}>
          <Modal isVisible={isModalVisible} isDark>
            <div className={styles.modalContainer}>
              <embed width="100%" height="100%" src={`data:application/pdf;base64,${agreementPDF.base64}`} />
            </div>
          </Modal>
          <div>
            <Box mt="lg" mb="md" width="100%" jc="sb">
              <SubTitle subTitle="Agreement Details" />
              {agreementDetails?.AgreementStatus === 'Agreement Not Generated' ||
              agreementDetails?.AgreementStatus === 'Agreement Voided' ? (
                <></>
              ) : (
                <div className={styles.dropdownContainer}>
                  <Button
                    id="agreement-actions-btn"
                    color="secondary"
                    label="Actions"
                    background="transparent"
                    textSize={1}
                    textColor="#2D2D2D"
                    noShadow
                    icon={isMenuVisible ? 'arrowTop' : 'arrowDown'}
                    iconLocation="right"
                    isDisabled={isRoleViewOnly}
                    onClick={handleMenu}
                  />
                  <DropDown isMenuVisible={isMenuVisible} onClick={setIsMenuVisible}>
                    <Button
                      id="view-agreement-btn"
                      label="View Agreement (PDF)"
                      background="transparent"
                      textColor="#2D2D2D"
                      textSize={1}
                      width="100%"
                      noShadow
                      onClick={() => {
                        setShouldViewAgreement(true);
                      }}
                    />
                    {agreementDetails?.AgreementStatus !== 'Agreement Completed' && (
                      <Button
                        id="void-agreement-btn"
                        label="Void Agreement"
                        background="transparent"
                        textColor="#2D2D2D"
                        textSize={1}
                        width="100%"
                        noShadow
                        isDisabled={isRoleViewOnly}
                        onClick={() => {
                          setShouldVoidAgreement(true);
                        }}
                      />
                    )}
                  </DropDown>
                </div>
              )}
            </Box>
            <Box pb="sm">
              <AgreementTable
                agreementDetails={agreementDetails}
                accountToken={accountToken}
                signingAuthorityInfo={signingAuthorityInfo}
                onClick={onClick}
              />
            </Box>
            <Box mt="lg">
              <Button
                id="view-signer-details"
                label="View Singer Details"
                color="primary"
                width="228px"
                noShadow
                round
                onClick={onClick}
              />
            </Box>
          </div>
          <AgreementDialogue handleDialogue={handlers.handleDialogue} />
        </AgreementContext.Provider>
      )}
    </>
  );
};

export default AgreementContainer;
